import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  isValid?: boolean;
  formSubmitted?: boolean;
  errorMessage?: string;
  className?: string;
  triggerClassName?: string;
  icon?: React.ReactNode;
}

const SelectField = ({
  id,
  label,
  value,
  onValueChange,
  options,
  placeholder = "Seleccionar...",
  required = false,
  isValid = true,
  formSubmitted = false,
  errorMessage = "Este campo es requerido",
  className = "",
  triggerClassName = "",
  icon,
}: SelectFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={`relative group ${className}`}>
      <Label 
        htmlFor={id} 
        className={`block text-xs xl:text-sm font-medium mb-2 transition-colors duration-200 ${
          !isValid && formSubmitted 
            ? "text-red-500" 
            : isFocused 
              ? "text-blue-600" 
              : "text-gray-700"
        }`}
      >
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
            <div className={`h-5 w-5 ${
              !isValid && formSubmitted 
                ? "text-red-500" 
                : isFocused 
                  ? "text-blue-600" 
                  : "text-gray-400"
            }`}>
              {icon}
            </div>
          </div>
        )}
        <Select 
          value={value} 
          onValueChange={(val) => {
            onValueChange(val);
            setIsFocused(false);
          }}
          onOpenChange={(open) => setIsFocused(open)}
        >
          <SelectTrigger 
            id={id}
            className={`w-full ${
              icon ? "pl-12" : "px-4"
            } rounded-lg border ${
              !isValid && formSubmitted 
                ? "border-red-500 focus-visible:ring-red-500" 
                : isFocused 
                  ? "border-blue-500 focus-visible:ring-blue-500" 
                  : "border-gray-300"
            } shadow-sm  h-10 xl:h-12 text-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-50 group-hover:border-gray-400 ${triggerClassName}`}
          >
            <SelectValue placeholder={<span className="text-sm text-gray-400">{placeholder}</span>} />
          </SelectTrigger>
          <SelectContent 
            position="popper" 
            className="rounded-lg border border-gray-200 shadow-md z-[9999999]"
            sideOffset={5}
          >
            {options.map((option) => (
              <SelectItem 
                key={option.value} 
                value={option.value}
                className="text-sm py-2.5 cursor-pointer"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!isValid && formSubmitted && (
        <p className="text-red-500 text-xs mt-2 flex items-center">
          <span className="inline-block mr-1">•</span>
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default SelectField; 